import { BLOCKED_ATTACHMENT_EXTENSIONS } from '../constants';

const BLOCKED_SET = new Set(BLOCKED_ATTACHMENT_EXTENSIONS.map((e) => e.toLowerCase()));

// Trailing characters stripped before extension matching. Windows and some save/unzip
// flows silently drop trailing dots, whitespace, and invisible/format characters,
// collapsing e.g. "malware.exe." or "malware.exe<ZWSP>" back to "malware.exe" -- so a
// file whose name ends with these must be matched as if they were absent, otherwise it
// is a trivial rename-based bypass of the blocklist. JS "\s" already covers NBSP, BOM
// and U+2000-U+200A; the explicit code points add the zero-width / bidi / format set
// that "\s" does NOT match: NEL (U+0085), soft hyphen (U+00AD), U+200B-200F,
// U+202A-202E, U+2060-2064.
const TRAILING_STRIP = /[.\s\u0085\u00ad\u200b-\u200f\u202a-\u202e\u2060-\u2064]+$/u;

/**
 * Returns the disallowed extension (lowercase, no dot) if `fileName` ends with a
 * blocklisted extension, otherwise null. Case-insensitive; matches only the FINAL
 * extension, so "invoice.pdf.exe" is blocked while "report.exe.pdf" is allowed.
 * The '.' boundary prevents substring false-positives (e.g. "notes.xhtml" is NOT
 * blocked by the "html" entry). Null-safe: a missing/blank name, or a name with no
 * extension, returns null (allowed).
 *
 * The name is NFKC-normalized and trailing dots/whitespace/format characters are stripped
 * before matching, so compatibility and rename-based evasions (fullwidth "malware.exe",
 * a trailing zero-width space, or a trailing dot "malware.exe.") still resolve to their
 * real extension.
 *
 * @param {string} fileName - The file's name (e.g. from File.name).
 * @returns {string|null} The matched blocked extension, or null if allowed.
 */
export function getBlockedAttachmentExtension(fileName) {
  const name = String(fileName ?? '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(TRAILING_STRIP, '');
  if (!name) return null;
  for (const ext of BLOCKED_SET) {
    if (name.endsWith('.' + ext)) return ext;
  }
  return null;
}
